/**
 * XR8Loader - Singleton script loader for the 8th Wall engine.
 *
 * 8th Wall is now open-source / self-hosted (Feb 2026). No API key required.
 * The engine files (xr.js, xr-slam.js, resources/) are served from
 * the hosting domain.
 *
 * For the StorySplat main app, files live at /cdn/8thwall/.
 * For self-hosted exports, a custom base URL can be set via:
 *   - ViewerConfig.eighthWallBaseUrl
 *   - data-8thwall-base attribute on the container or <html>
 *
 * Engine source: https://github.com/8thwall/engine
 */

import type { XR8Global } from './XR8Types';
import { arError, arInfo } from './ARLogger';

/** Default timeout waiting for XR8 to initialize (ms) */
const XR8_LOAD_TIMEOUT = 15000;

/** Default self-hosted path (relative to domain root) */
const DEFAULT_BASE_URL = '/cdn/8thwall';

/** Singleton promise — only one load attempt ever happens */
let loadPromise: Promise<XR8Global> | null = null;

/**
 * Resolve the base URL for 8th Wall engine files.
 *
 * Priority:
 *   1. Explicit baseUrl parameter (from ViewerConfig.eighthWallBaseUrl)
 *   2. data-8thwall-base attribute on the container element
 *   3. data-8thwall-base attribute on <html>
 *   4. Default: /cdn/8thwall (self-hosted on same domain)
 */
function resolveBaseUrl(container?: HTMLElement, explicitBaseUrl?: string): string {
    if (explicitBaseUrl) return explicitBaseUrl.replace(/\/$/, '');

    const fromContainer = container?.getAttribute('data-8thwall-base');
    if (fromContainer) return fromContainer.replace(/\/$/, '');

    const fromRoot = document.documentElement.getAttribute('data-8thwall-base');
    if (fromRoot) return fromRoot.replace(/\/$/, '');

    return DEFAULT_BASE_URL;
}

/** Script download limit (the engine is ~1 MB; slow networks get a generous margin). */
const SCRIPT_LOAD_TIMEOUT = 45000;

/**
 * Inject a <script> tag and resolve once it has loaded. A tag that failed (or timed out) is
 * removed, so a later attempt really downloads again instead of finding the dead tag and waiting
 * for an engine that never arrives.
 */
function injectScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null;
        if (existing?.dataset.storysplatLoaded === 'true') {
            resolve();
            return;
        }
        existing?.remove();

        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        const fail = (message: string) => {
            clearTimeout(timer);
            script.remove();
            reject(new Error(message));
        };
        const timer = setTimeout(() => fail(`[StorySplat] Timed out loading 8th Wall script: ${src}`), SCRIPT_LOAD_TIMEOUT);
        script.onload = () => {
            clearTimeout(timer);
            script.dataset.storysplatLoaded = 'true';
            resolve();
        };
        script.onerror = () => fail(`[StorySplat] Failed to load 8th Wall script: ${src}`);
        document.head.appendChild(script);
    });
}

/**
 * Wait for the global XR8 object to become available.
 * 8th Wall fires a custom 'xrloaded' event on window when ready.
 */
function waitForXR8(timeoutMs: number = XR8_LOAD_TIMEOUT): Promise<XR8Global> {
    return new Promise((resolve, reject) => {
        // Already available
        if (window.XR8) {
            resolve(window.XR8);
            return;
        }

        const timer = setTimeout(() => {
            window.removeEventListener('xrloaded', onLoaded);
            reject(new Error(`[StorySplat] 8th Wall engine did not initialize within ${timeoutMs}ms`));
        }, timeoutMs);

        function onLoaded() {
            clearTimeout(timer);
            window.removeEventListener('xrloaded', onLoaded);
            if (window.XR8) {
                resolve(window.XR8);
            } else {
                reject(new Error('[StorySplat] xrloaded event fired but XR8 global not found'));
            }
        }

        window.addEventListener('xrloaded', onLoaded);
    });
}

/**
 * Load the 8th Wall engine and SLAM module.
 * Returns a promise that resolves to the XR8 global object.
 *
 * This is a singleton — calling it multiple times returns the same promise.
 *
 * @param container - Optional container element to check for data-8thwall-base attribute
 * @param baseUrl - Optional explicit base URL for the engine files
 */
export function loadXR8(container?: HTMLElement, baseUrl?: string): Promise<XR8Global> {
    if (loadPromise) return loadPromise;

    loadPromise = (async () => {
        const resolvedBase = resolveBaseUrl(container, baseUrl);
        arInfo('Loading 8th Wall engine', { baseUrl: resolvedBase });

        // Load the core runtime (unless the host page already has it)
        if (!window.XR8) await injectScript(`${resolvedBase}/xr.js`);

        // Wait for XR8 global to appear
        const XR8 = await waitForXR8();

        // Load SLAM chunk for world tracking (ground detection)
        arInfo('Loading 8th Wall SLAM module');
        await XR8.loadChunk('slam');

        // Configure controller for ground placement
        // IMPORTANT: Use 'absolute' scale for proper ground detection
        XR8.XrController.configure({ scale: 'absolute' });

        arInfo('8th Wall engine loaded', {
            version: XR8.version(),
            hasPlayCanvasWrapper: !!XR8.PlayCanvas?.run,
            hasXrController: !!XR8.XrController,
        });
        return XR8;
    })();

    // Reset singleton on failure so retry is possible
    loadPromise.catch((err) => {
        arError('8th Wall engine load failed', err);
        loadPromise = null;
    });

    return loadPromise;
}

/**
 * Check if 8th Wall is already loaded and available.
 */
export function isXR8Available(): boolean {
    return !!window.XR8;
}

/**
 * Check if native WebXR AR is available (used to decide whether to fall back to 8th Wall).
 */
export function isNativeWebXRAvailable(): Promise<boolean> {
    return new Promise((resolve) => {
        if (!navigator.xr) {
            resolve(false);
            return;
        }
        navigator.xr.isSessionSupported('immersive-ar')
            .then(supported => resolve(supported))
            .catch(() => resolve(false));
    });
}
